import { X } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useEffect, useId, useRef } from "react";
import { cx } from "../cx";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function focusablesWithin(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element): element is HTMLElement => element instanceof HTMLElement,
  );
}

export interface DialogProps {
  open: boolean;
  /** Called on Escape, scrim click, and the close button. */
  onClose?: (() => void) | undefined;
  title?: ReactNode | undefined;
  children?: ReactNode | undefined;
  /** Right-aligned action row. */
  footer?: ReactNode | undefined;
  /** Panel width in px (default 480). */
  width?: number | undefined;
  className?: string | undefined;
}

/**
 * Modal dialog — the only shadow-3 surface. Moves focus inside on open,
 * traps Tab within the panel, closes on Escape and scrim click, and restores
 * focus on close. Ported from design/tarski-kit/components/overlay/Dialog.jsx.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  width = 480,
  className,
}: DialogProps): JSX.Element | null {
  const titleId = useId();
  const scrimRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Move focus inside on open; restore it to the opener on close.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const previous = document.activeElement;
    const panel = panelRef.current;
    if (panel !== null) {
      const first = focusablesWithin(panel)[0];
      if (first !== undefined) {
        first.focus();
      } else {
        panel.focus();
      }
    }
    return () => {
      if (previous instanceof HTMLElement && previous.isConnected) {
        previous.focus();
      }
    };
  }, [open]);

  // Escape closes; Tab cycles within the panel (simple focus trap).
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose?.();
        return;
      }
      if (event.key !== "Tab") {
        return;
      }
      const panel = panelRef.current;
      if (panel === null) {
        return;
      }
      const focusables = focusablesWithin(panel);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (first === undefined || last === undefined) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const active = document.activeElement;
      if (active === null || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Clicking the scrim itself (not the panel) closes.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const scrim = scrimRef.current;
    if (scrim === null) {
      return undefined;
    }
    const handleClick = (event: MouseEvent) => {
      if (event.target === scrim) {
        onClose?.();
      }
    };
    scrim.addEventListener("click", handleClick);
    return () => scrim.removeEventListener("click", handleClick);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div ref={scrimRef} className="tk-dialog-scrim" role="presentation">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title === undefined ? undefined : titleId}
        tabIndex={-1}
        className={cx("tk-dialog", className)}
        style={{ width }}
      >
        <div className="tk-dialog__header">
          <h2 className="tk-dialog__title" id={titleId}>
            {title}
          </h2>
          <button type="button" className="tk-dialog__close" aria-label="Close" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="tk-dialog__body">{children}</div>
        {footer === undefined ? null : <div className="tk-dialog__footer">{footer}</div>}
      </div>
    </div>
  );
}
